import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ChevronLeft, AlertCircle, Clock } from "lucide-react";
import { CalendarPicker, SlotPicker, fmtDateLong, isValidEmail, isValidPhone } from "@/components/booking/CalendarSlots";
import { trackFunnel } from "@/lib/funnel";

const PAGE = "verify-your-eligibility";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_KEY = "bc_ve_contact";
const TOTAL = 11; // calendar + name + number + email + 7 questions (one branch shares a slot)

const BLOCKERS = [
  "I'm not clear about my niche / target audience",
  "I'm not getting enough qualified leads",
  "I'm getting leads, but not enough sales",
  "My ads / funnel aren't performing",
  "I'm struggling to stand out from competitors",
  "I'm getting customers, but growth isn't predictable",
  "I'm not sure what's actually going wrong",
];

const BUSINESS_AGE = ["0 – 6 months", "6 months – 1 year", "1 – 2 years", "2 – 5 years"];

const REVENUE_OPTIONS = [
  "Not yet started",
  "Up to ₹ 50,000",
  "₹ 50,000 – ₹ 1 Lakh",
  "₹ 1 Lakh – ₹ 2.5 Lakh",
  "₹ 2.5 Lakh – ₹ 5 Lakh",
  "₹ 5 Lakh – ₹ 10 Lakh",
  "₹ 10 Lakh – ₹ 25 Lakh",
  "₹ 25 Lakh & above",
];

const AD_BUDGETS = [
  "Up to ₹ 50,000 / month",
  "₹ 50,000 – ₹ 1 Lakh / month",
  "₹ 1 Lakh – ₹ 2.5 Lakh / month",
  "₹ 2.5 Lakh – ₹ 5 Lakh / month",
  "₹ 5 Lakh & above / month",
];

const GOALS = [
  "Up to ₹ 5 Lakh / month",
  "₹ 5 Lakh – ₹ 10 Lakh / month",
  "₹ 10 Lakh – ₹ 25 Lakh / month",
  "₹ 25 Lakh – ₹ 50 Lakh / month",
  "₹ 50 Lakh – ₹ 1 Cr / month",
  "₹ 1 Cr+ / month",
];

const LETTERS = "ABCDEFGH";
const bigInput =
  "w-full bg-transparent border-b-2 border-white/15 focus:border-red-600 text-white text-xl md:text-2xl py-3 outline-none placeholder:text-zinc-700 transition-colors";
const qLabel = "block text-xs md:text-sm c-muted tracking-[0.08em] mb-2";

function Progress({ index }) {
  const pct = (index / TOTAL) * 100;
  return (
    <div className="mb-10" data-testid="sc-progress">
      <div className="h-[3px] bg-white/8" style={{ borderRadius: 0 }}>
        <div className="h-full bg-red-600 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function QHead({ kicker, children, sub }) {
  return (
    <>
      <div className="c-eyebrow mb-4" data-testid="sc-q-kicker">{kicker}</div>
      <h2
        className="text-white"
        style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 700, fontSize: "clamp(30px, 6vw, 46px)", lineHeight: 1.08 }}
        data-testid="sc-question"
      >
        {children}
      </h2>
      {sub ? <p className="c-muted text-sm md:text-base mt-4 mb-2 leading-relaxed">{sub}</p> : null}
      <div className="mb-9" />
    </>
  );
}

function Hint({ show, children }) {
  if (!show) return null;
  return (
    <p className="mt-4 text-sm text-amber-400/90 flex items-center gap-1.5" data-testid="sc-hint">
      <AlertCircle size={14} /> {children}
    </p>
  );
}

// Subtle, clean option tiles (sans-serif, light weight)
function OptionTile({ label, letter, selected, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-4 py-3.5 px-5 border text-left transition-all ${
        selected ? "border-red-600 bg-red-700/10 text-white" : "border-white/12 text-zinc-300 hover:border-white/35 hover:text-white"
      }`}
      style={{ borderRadius: 0 }}
      data-testid={testId}
    >
      <span
        className={`flex items-center justify-center w-7 h-7 border text-xs flex-shrink-0 ${
          selected ? "border-red-500 text-red-300" : "border-white/20 text-zinc-500"
        }`}
        style={{ borderRadius: 0 }}
      >
        {letter}
      </span>
      <span className="text-[15px] md:text-base font-normal tracking-[0.01em]">{label}</span>
    </button>
  );
}

function BigYesNo({ value, onSelect, yesId, noId }) {
  const opt = (val, label, letter, id) => (
    <button
      onClick={() => onSelect(val)}
      className={`flex-1 flex items-center gap-3 py-4 px-6 border transition-all ${
        value === val ? "border-red-600 bg-red-700/10 text-white" : "border-white/12 text-zinc-300 hover:border-white/35 hover:text-white"
      }`}
      style={{ borderRadius: 0 }}
      data-testid={id}
    >
      <span className={`flex items-center justify-center w-7 h-7 border text-xs flex-shrink-0 ${value === val ? "border-red-500 text-red-300" : "border-white/20 text-zinc-500"}`} style={{ borderRadius: 0 }}>{letter}</span>
      <span className="text-base md:text-lg font-normal">{label}</span>
    </button>
  );
  return <div className="flex gap-3">{opt("yes", "Yes", "A", yesId)}{opt("no", "No", "B", noId)}</div>;
}

function SlotChip({ date, time, onChange, testId, changeTestId }) {
  return (
    <div className="border border-white/10 p-4 flex items-center justify-between gap-3" style={{ borderRadius: 0 }} data-testid={testId}>
      <div className="flex items-center gap-3 text-base text-white">
        <Clock size={16} className="c-red flex-shrink-0" />
        <span>{fmtDateLong(date)} · <span className="c-red">{time} IST</span></span>
      </div>
      <button onClick={onChange} className="text-xs c-muted hover:text-white flex-shrink-0 flex items-center gap-1" data-testid={changeTestId}>
        <ChevronLeft size={13} /> Change
      </button>
    </div>
  );
}

function NavRow({ onBack, onNext, nextLabel = "Continue", nextDisabled, backShown = true, nextTestId, backTestId }) {
  return (
    <div className="flex items-center justify-between gap-3 mt-10">
      {backShown ? (
        <button onClick={onBack} className="text-sm c-muted hover:text-white flex items-center gap-1 py-2" data-testid={backTestId}>
          <ChevronLeft size={16} /> Back
        </button>
      ) : <span />}
      <button
        onClick={onNext}
        disabled={nextDisabled}
        className="c-btn-primary text-sm py-4 px-8 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ minHeight: 56 }}
        data-testid={nextTestId}
      >
        {nextLabel} <ChevronRight size={16} />
      </button>
    </div>
  );
}

const anim = {
  initial: { opacity: 0, x: 28 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -28 },
  transition: { duration: 0.32, ease: "easeOut" },
};

export default function StrategyCallPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Load any saved contact details (so a returning visitor keeps their info)
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(CONTACT_KEY) || "{}"); } catch (e) { return {}; }
  })();

  const [name, setName] = useState(saved.name || "");
  const [phone, setPhone] = useState(saved.phone || "");
  const [email, setEmail] = useState(saved.email || "");
  const [businessAge, setBusinessAge] = useState("");
  const [blocker, setBlocker] = useState("");
  const [adBudget, setAdBudget] = useState("");
  const [revenue, setRevenue] = useState("");
  const [runningAds, setRunningAds] = useState(null);
  const [budgetOk, setBudgetOk] = useState(null);
  const [investBudget, setInvestBudget] = useState("");

  const [settings, setSettings] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [hint, setHint] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [disqualified, setDisqualified] = useState(false);
  const [goal, setGoal] = useState("");
  const [website, setWebsite] = useState("");

  const nameValid = Boolean(name.trim());
  const phoneValid = isValidPhone(phone);
  const emailValid = isValidEmail(email);

  // Persist contact details as the user types
  useEffect(() => {
    try { localStorage.setItem(CONTACT_KEY, JSON.stringify({ name, phone, email })); } catch (e) { /* swallow */ }
  }, [name, phone, email]);

  const capturePartialLead = async () => {
    if (leadId || !name.trim() || !isValidPhone(phone)) return;
    try {
      const { data } = await axios.post(`${API}/booking/partial-lead`, {
        name: name.trim(),
        whatsapp: phone.trim(),
        source: sessionStorage.getItem("bc_booking_source") || "strategy_call_page",
      });
      if (data?.id) setLeadId(data.id);
    } catch (e) { /* never block the user */ }
  };

  const back = () => {
    setHint(false);
    setStep((s) => {
      if (s === 12) return runningAds === "yes" ? 10 : 13;
      if (s === 13) return 11;
      if (s === 11 || s === 10) return 9;
      return Math.max(1, s - 1);
    });
  };

  const progressIndex = step <= 9 ? step : step === 12 ? 11 : 10;

  const goCalendar = () => { if (!selectedTime) { setHint(true); return; } setHint(false); setStep(2); };
  const goName = () => { if (!nameValid) { setHint(true); return; } setHint(false); setStep(3); };
  const goPhone = () => { if (!phoneValid) { setHint(true); return; } setHint(false); capturePartialLead(); setStep(4); };
  const goEmail = () => { if (!emailValid) { setHint(true); return; } setHint(false); trackFunnel("step1_complete", PAGE, { dedupe: true }); capturePartialLead(); setStep(5); };

  const pickGoal = (g) => { setGoal(g); setTimeout(() => setStep(6), 240); };
  const pickBlocker = (b) => { setBlocker(b); setTimeout(() => setStep(7), 240); };
  const pickAge = (a) => { setBusinessAge(a); setTimeout(() => setStep(8), 240); };
  const pickRevenue = (r) => { setRevenue(r); setTimeout(() => setStep(9), 240); };
  const pickAds = (v) => { setRunningAds(v); setTimeout(() => setStep(v === "yes" ? 10 : 11), 240); };
  const pickAdBudget = (a) => { setAdBudget(a); setTimeout(() => setStep(12), 240); };
  const pickMetaAds = (v) => {
    setBudgetOk(v);
    if (v === "no") { disqualify("Not willing to invest in an ad budget"); return; }
    setTimeout(() => setStep(13), 240);
  };

  const allAnswered =
    Boolean(selectedDate) && Boolean(selectedTime) && nameValid && phoneValid && emailValid &&
    Boolean(goal) && Boolean(blocker) && Boolean(businessAge) && Boolean(revenue) &&
    runningAds !== null && (runningAds === "yes" ? Boolean(adBudget) : (budgetOk === "yes" && Boolean(investBudget.trim()))) &&
    Boolean(website.trim());

  const disqualify = async (reason) => {
    trackFunnel("disqualified", PAGE, { dedupe: true });
    setDisqualified(true);
    try {
      await axios.post(`${API}/booking/disqualified-lead`, {
        name: name.trim(),
        email: email.trim(),
        whatsapp: phone.trim(),
        revenue_band: reason,
        source: sessionStorage.getItem("bc_booking_source") || "strategy_call_page",
      });
    } catch (e) { /* swallow */ }
  };

  useEffect(() => {
    document.title = "Verify Your Eligibility — BrandChilly";
    axios.get(`${API}/booking/settings`).then((r) => setSettings(r.data)).catch(() => setError("Could not load booking settings"));
    try {
      if (window.fbq) window.fbq("track", "InitiateCheckout", { content_name: "StrategyCall" });
      if (window.gtag) window.gtag("event", "begin_checkout", { content_name: "StrategyCall" });
    } catch (e) { /* swallow */ }
    trackFunnel("page_view", PAGE, { dedupe: true });
    trackFunnel("form_start", PAGE, { dedupe: true });
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    axios.get(`${API}/booking/slots?date=${selectedDate}`)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate]);

  const confirmBooking = async () => {
    if (!allAnswered) { setError("Please answer all the questions before we can book your call."); setHint(true); return; }
    setSubmitting(true);
    setError("");
    try {
      let engagement = {};
      try {
        const e = JSON.parse(sessionStorage.getItem("bc_fc_engagement") || "null");
        if (e) engagement = { time_on_page: e.time_on_page, scroll_percent: e.scroll_percent, video_watch_percent: e.video_watch_percent };
      } catch (err) { /* swallow */ }
      const payload = {
        date: selectedDate,
        time: selectedTime,
        name: name.trim(),
        whatsapp: phone.trim(),
        email: email.trim(),
        ...engagement,
        goal: goal,
        business_age: businessAge,
        growth_blocker: blocker,
        revenue_band: revenue,
        running_ads: runningAds === "yes" ? "Yes" : "No",
        ad_budget: runningAds === "yes" ? adBudget : investBudget.trim(),
        website: website.trim(),
        source: sessionStorage.getItem("bc_booking_source") || "strategy_call_page",
        incomplete_lead_id: leadId || undefined,
      };
      const { data } = await axios.post(`${API}/booking`, payload);
      trackFunnel("booking_confirmed", PAGE, { dedupe: true });
      sessionStorage.removeItem("bc_booking_source");
      sessionStorage.removeItem("bc_fc_engagement");
      try { localStorage.removeItem(CONTACT_KEY); } catch (e) { /* swallow */ }
      navigate("/confirmed", { state: { booking: data } });
    } catch (err) {
      if (err?.response?.status === 409) {
        setError("That slot was just taken. Please pick another time.");
        setSelectedTime(null);
      } else {
        setError(err?.response?.data?.detail || "Could not complete booking. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="coaches-page" style={{ minHeight: "100vh", background: "var(--c-bg)" }}>
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 md:h-24 flex items-center justify-between">
          <a href="/" className="flex items-center" data-testid="sc-logo">
            <img src="/assets/logo_header.png" alt="BrandChilly" className="h-11 md:h-14 w-auto" />
          </a>
          <div className="text-[10px] md:text-[11px] c-muted tracking-[0.18em] uppercase">All times in IST</div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 md:px-8 py-10 md:py-16">
        {!disqualified && <Progress index={progressIndex} />}

        {disqualified ? (
          <motion.div {...anim} data-testid="sc-disqualified" className="text-center py-6">
            <div className="c-eyebrow mb-4">Thanks for your honesty</div>
            <h2 className="text-white mb-5" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 700, fontSize: "clamp(28px, 6vw, 44px)", lineHeight: 1.1 }}>
              We're not the right fit <span className="c-red italic">just yet.</span>
            </h2>
            <p className="c-muted text-sm md:text-base leading-relaxed max-w-lg mx-auto">
              Our system is built around paid acquisition — we put your offer in front of the right buyers with ads,
              then convert them into clients. Without an ad budget to work with, we can't deliver the kind of
              predictable growth we'd want to. If that changes down the line, come back — we'd love to help you scale.
            </p>
            <a href="/for-coaches" className="btn-ghost inline-flex mt-8" data-testid="sc-dq-back">
              <ChevronLeft size={16} /> Back to the overview
            </a>
          </motion.div>
        ) : (
          <AnimatePresence mode="wait">
            {/* STEP 1 — CALENDAR (first) */}
            {step === 1 && (
              <motion.div key="s1" {...anim} data-testid="sc-calendar">
                <QHead kicker="Step 1 · Pick your slot" sub="Choose a time when you're free for a 30–40 minute call. You'll answer a few quick questions next — your booking is confirmed only after that.">
                  When works best for your call?
                </QHead>

                {settings && (
                  !selectedDate ? (
                    <CalendarPicker settings={settings} selectedDate={selectedDate} onPick={(d) => setSelectedDate(d)} />
                  ) : !selectedTime ? (
                    <SlotPicker date={selectedDate} slots={slots} loading={slotsLoading} onBack={() => setSelectedDate(null)} onPick={(t) => { setSelectedTime(t); trackFunnel("slot_picked", PAGE, { dedupe: true }); }} />
                  ) : (
                    <SlotChip date={selectedDate} time={selectedTime} onChange={() => setSelectedTime(null)} testId="sc-slot-chip" changeTestId="sc-change-slot" />
                  )
                )}
                <Hint show={hint && !selectedTime}>Please pick a date and time to continue.</Hint>
                <NavRow backShown={false} onNext={goCalendar} nextDisabled={!selectedTime} nextTestId="sc-cal-next" />
              </motion.div>
            )}

            {/* STEP 2 — NAME */}
            {step === 2 && (
              <motion.div key="s2" {...anim} data-testid="sc-step-name">
                <QHead kicker="Your details" sub="A few quick questions so we can prepare for your call. We only work with businesses we're confident we can deliver results to.">
                  First, what's your name?
                </QHead>
                <label className={qLabel}>Full Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && goName()} placeholder="Your full name" className={bigInput} data-testid="sc-name" autoFocus />
                <Hint show={hint && !nameValid}>Please enter your name.</Hint>
                <NavRow onBack={back} onNext={goName} nextTestId="sc-name-next" backTestId="sc-name-back" />
              </motion.div>
            )}

            {/* STEP 3 — NUMBER */}
            {step === 3 && (
              <motion.div key="s3" {...anim} data-testid="sc-step-phone">
                <QHead kicker="Your details">What's your WhatsApp number?</QHead>
                <label className={qLabel}>Contact Number (WhatsApp)</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && goPhone()} placeholder="+91 98765 43210" className={bigInput} data-testid="sc-phone" autoFocus />
                <Hint show={hint && !phoneValid}>Please enter a valid WhatsApp number.</Hint>
                <NavRow onBack={back} onNext={goPhone} nextTestId="sc-phone-next" backTestId="sc-phone-back" />
              </motion.div>
            )}

            {/* STEP 4 — EMAIL */}
            {step === 4 && (
              <motion.div key="s4" {...anim} data-testid="sc-step-email">
                <QHead kicker="Your details">And your best email?</QHead>
                <label className={qLabel}>Email Address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && goEmail()} placeholder="you@yourbrand.com" className={bigInput} data-testid="sc-email" autoFocus />
                <Hint show={hint && !emailValid}>Please enter a valid email address.</Hint>
                <NavRow onBack={back} onNext={goEmail} nextTestId="sc-email-next" backTestId="sc-email-back" />
              </motion.div>
            )}

            {/* STEP 5 — GOAL */}
            {step === 5 && (
              <motion.div key="s5" {...anim} data-testid="sc-step-goal">
                <QHead kicker="Question 1 of 7">What's your revenue goal?</QHead>
                <div className="flex flex-col gap-3">
                  {GOALS.map((g, i) => (
                    <OptionTile key={g} label={g} letter={LETTERS[i]} selected={goal === g} onClick={() => pickGoal(g)} testId={`sc-goal-${i}`} />
                  ))}
                </div>
                <NavRow onBack={back} onNext={() => goal && setStep(6)} nextDisabled={!goal} backTestId="sc-goal-back" nextTestId="sc-goal-next" />
              </motion.div>
            )}

            {/* STEP 6 — #1 GROWTH BLOCKER */}
            {step === 6 && (
              <motion.div key="s6" {...anim} data-testid="sc-step-blocker">
                <QHead kicker="Question 2 of 7">What's the #1 thing stopping you from growing faster right now?</QHead>
                <div className="flex flex-col gap-3">
                  {BLOCKERS.map((b, i) => (
                    <OptionTile key={b} label={b} letter={LETTERS[i]} selected={blocker === b} onClick={() => pickBlocker(b)} testId={`sc-blocker-${i}`} />
                  ))}
                </div>
                <NavRow onBack={back} onNext={() => blocker && setStep(7)} nextDisabled={!blocker} backTestId="sc-blocker-back" nextTestId="sc-blocker-next" />
              </motion.div>
            )}

            {/* STEP 7 — BUSINESS AGE */}
            {step === 7 && (
              <motion.div key="s7" {...anim} data-testid="sc-step-age">
                <QHead kicker="Question 3 of 7">How old is your business?</QHead>
                <div className="flex flex-col gap-3">
                  {BUSINESS_AGE.map((a, i) => (
                    <OptionTile key={a} label={a} letter={LETTERS[i]} selected={businessAge === a} onClick={() => pickAge(a)} testId={`sc-age-${i}`} />
                  ))}
                </div>
                <NavRow onBack={back} onNext={() => businessAge && setStep(8)} nextDisabled={!businessAge} backTestId="sc-age-back" nextTestId="sc-age-next" />
              </motion.div>
            )}

            {/* STEP 8 — REVENUE */}
            {step === 8 && (
              <motion.div key="s8" {...anim} data-testid="sc-step-revenue">
                <QHead kicker="Question 4 of 7">What's your current monthly revenue?</QHead>
                <div className="flex flex-col gap-3">
                  {REVENUE_OPTIONS.map((r, i) => (
                    <OptionTile key={r} label={r} letter={LETTERS[i]} selected={revenue === r} onClick={() => pickRevenue(r)} testId={`sc-revenue-${i}`} />
                  ))}
                </div>
                <NavRow onBack={back} onNext={() => revenue && setStep(9)} nextDisabled={!revenue} backTestId="sc-revenue-back" nextTestId="sc-revenue-next" />
              </motion.div>
            )}

            {/* STEP 9 — RUNNING ADS */}
            {step === 9 && (
              <motion.div key="s9" {...anim} data-testid="sc-step-ads">
                <QHead kicker="Question 5 of 7">Are you currently running ads?</QHead>
                <BigYesNo value={runningAds} onSelect={pickAds} yesId="sc-ads-yes" noId="sc-ads-no" />
                <NavRow onBack={back} onNext={() => runningAds !== null && setStep(runningAds === "yes" ? 10 : 11)} nextDisabled={runningAds === null} backTestId="sc-ads-back" nextTestId="sc-ads-next" />
              </motion.div>
            )}

            {/* STEP 10 — MONTHLY AD BUDGET (only if running ads) */}
            {step === 10 && (
              <motion.div key="s10" {...anim} data-testid="sc-step-adbudget">
                <QHead kicker="Question 6 of 7">What's your current monthly ad budget?</QHead>
                <div className="flex flex-col gap-3">
                  {AD_BUDGETS.map((a, i) => (
                    <OptionTile key={a} label={a} letter={LETTERS[i]} selected={adBudget === a} onClick={() => pickAdBudget(a)} testId={`sc-adbudget-${i}`} />
                  ))}
                </div>
                <NavRow onBack={back} onNext={() => adBudget && setStep(12)} nextDisabled={!adBudget} backTestId="sc-adbudget-back" nextTestId="sc-adbudget-next" />
              </motion.div>
            )}

            {/* STEP 11 — OKAY TO INVEST IN ADS (only if NOT running ads) */}
            {step === 11 && (
              <motion.div key="s11" {...anim} data-testid="sc-step-budget">
                <QHead
                  kicker="Question 6 of 7"
                  sub="To hit the goal you picked, we put your offer in front of the right buyers with paid ads — that's what makes growth predictable. The exact amount depends on your market, and we'll advise what fits your case on the call."
                >
                  We'll need to run ads to reach your goal. Are you okay investing in an ad budget?
                </QHead>
                <BigYesNo value={budgetOk} onSelect={pickMetaAds} yesId="sc-budget-yes" noId="sc-budget-no" />
                <NavRow onBack={back} onNext={() => { if (budgetOk === "no") { disqualify("Not willing to invest in an ad budget"); return; } if (budgetOk === "yes") setStep(13); }} nextDisabled={budgetOk === null} backTestId="sc-budget-back" nextTestId="sc-budget-next" />
              </motion.div>
            )}

            {/* STEP 13 — HOW MUCH CAN YOU INVEST (short answer, only if open to investing) */}
            {step === 13 && (
              <motion.div key="s13" {...anim} data-testid="sc-step-invest">
                <QHead kicker="Question 6 of 7" sub="A rough monthly number is fine — it just helps us tailor the plan we bring to your call.">
                  How much are you comfortable investing per month?
                </QHead>
                <label className={qLabel}>Monthly ad budget you're open to</label>
                <input type="text" value={investBudget} onChange={(e) => setInvestBudget(e.target.value)} onKeyDown={(e) => e.key === "Enter" && investBudget.trim() && setStep(12)} placeholder="e.g. ₹ 50,000 / month" className={bigInput} data-testid="sc-invest" autoFocus />
                <Hint show={hint && !investBudget.trim()}>Please share a rough monthly budget.</Hint>
                <NavRow onBack={back} onNext={() => { if (!investBudget.trim()) { setHint(true); return; } setHint(false); setStep(12); }} nextDisabled={!investBudget.trim()} backTestId="sc-invest-back" nextTestId="sc-invest-next" />
              </motion.div>
            )}

            {/* STEP 12 — WEBSITE + CONFIRM (final) */}
            {step === 12 && (
              <motion.div key="s12" {...anim} data-testid="sc-step-website">
                <QHead kicker="Question 7 of 7" sub="Share your website or main social handle so we can review your brand before the call.">
                  Where can we find you online?
                </QHead>
                <label className={qLabel}>Website or social link</label>
                <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirmBooking()} placeholder="yourbrand.com or instagram.com/yourhandle" className={bigInput} data-testid="sc-website" autoFocus />
                <Hint show={hint && !website.trim()}>Please add your website or a social link.</Hint>

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm mt-6" data-testid="sc-error">
                    <AlertCircle size={14} /> {error}
                  </div>
                )}

                <div className="flex items-center justify-between gap-3 mt-10">
                  <button onClick={back} className="text-sm c-muted hover:text-white flex items-center gap-1 py-2" data-testid="sc-website-back">
                    <ChevronLeft size={16} /> Back
                  </button>
                  <button
                    onClick={confirmBooking}
                    disabled={submitting}
                    className="c-btn-primary text-sm py-4 px-8 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ minHeight: 56 }}
                    data-testid="sc-confirm-btn"
                  >
                    {submitting ? "Confirming…" : "Confirm My Call"} <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </main>
    </div>
  );
}
